import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { ApiAccountService } from '@services/api-account.service';

import { RegisterModel } from '@models/RegisterModel';

/**
 * Verificación por código previa a crear la cuenta, igual que el registro
 * original de Givi.
 *
 * `checkemail/v1` y `checkphone/v1` envían un código que el API calcula como
 * ComputeCode("<email|país teléfono>-<año-mes-día>") usando el `requestAt` del
 * modelo. Ese mismo código viaja después en `codeConfirmation` al crear la
 * cuenta, y el backend lo recalcula para validarlo.
 *
 * Por eso acá no se valida nada: el componente envía el código y lo devuelve al
 * padre, que es quien crea la cuenta. Si el API lo rechaza, el padre avisa con
 * `errorMessage`.
 */
@Component({
    selector: 'app-account-otp-verify',
    templateUrl: './account-otp-verify.component.html',
    styleUrls: ['./account-otp-verify.component.scss']
})
export class AccountOtpVerifyComponent implements OnInit, OnDestroy {

    /** Datos del registro. Debe traer `requestAt`: el código depende de esa fecha. */
    @Input() register: RegisterModel;

    /** Texto del botón principal, para que cada flujo diga lo suyo. */
    @Input() confirmLabel = 'Verificar y continuar';

    /** Error devuelto por el padre al crear la cuenta (código inválido, etc.). */
    @Input() errorMessage: string = null;

    /** El padre está creando la cuenta: bloquea el botón. */
    @Input() working = false;

    /** Código ingresado, ya limpio. */
    @Output() codeConfirmed = new EventEmitter<string>();

    code = '';
    localError: string = null;

    sending = false;

    /** Canales por los que salió el último envío, para el texto de arriba. */
    sentTo = { email: false, whatsApp: false };

    /** Segundos que faltan para poder reenviar. */
    secondsLeft = 0;

    /** Ya se envió al menos una vez: cambia el texto del link. */
    private alreadySent = false;

    private timer: any = null;

    private readonly waitSeconds = 45;

    constructor(private apiAccountService: ApiAccountService) { }

    ngOnInit(): void {

        this.sendCode();
    }

    ngOnDestroy(): void {

        if (this.timer) clearInterval(this.timer);
    }

    get errorText(): string {

        return this.errorMessage || this.localError;
    }

    get maskedEmail(): string {

        const email = String(this.register?.email ?? '');
        const [user, domain] = email.split('@');

        if (!user || !domain) return email;

        // Cantidad fija de asteriscos: repetir el largo real daba tiras desparejas
        // y de paso filtraba cuán largo es el correo.
        const visible = user.length <= 2 ? user : `${user[0]}${'*'.repeat(4)}${user.slice(-1)}`;

        return `${visible}@${domain}`;
    }

    get maskedPhone(): string {

        const digits = String(this.register?.phone ?? '').replace(/\D/g, '');

        if (digits.length <= 4) return digits;

        return `${'*'.repeat(4)}${digits.slice(-4)}`;
    }

    get canSend(): boolean {

        return this.secondsLeft <= 0 && !this.sending;
    }

    get resendLabel(): string {

        if (this.secondsLeft > 0) return `Reenviar en ${this.secondsLeft}s`;

        return this.alreadySent ? 'Reenviar código' : 'Enviarme el código';
    }

    /** Un solo envío: el API manda el mismo código por correo y por WhatsApp. */
    sendCode(): void {

        if (!this.canSend || !this.register) return;

        this.sending = true;
        this.localError = null;

        this.apiAccountService.verifyAccount(this.register).then(
            (res) => {

                this.sending = false;
                this.alreadySent = true;
                this.sentTo = { email: !!res?.email, whatsApp: !!res?.whatsApp };
                this._startCountdown();
            },
            (err: any) => {

                this.sending = false;

                // 429: se pidió el código demasiadas veces. El API manda el texto con
                // cuánto hay que esperar, así que se muestra tal cual.
                if (err?.status === 429) {

                    this.localError = typeof err?.error === 'string' && err.error.length
                        ? err.error
                        : 'Pediste el código demasiadas veces. Esperá unos minutos e intentá de nuevo.';

                    // Se bloquea el reenvío para que no siga golpeando el endpoint.
                    this._startCountdown();
                    return;
                }

                // El API responde 400 con el texto cuando el correo o el teléfono
                // ya están vinculados a otra cuenta.
                this.localError = typeof err?.error === 'string' && err.error.length
                    ? err.error
                    : 'No pudimos enviar el código. Intentá de nuevo en unos minutos.';
            }
        );
    }

    onConfirm(): void {

        // Puede pegarse con espacios desde el correo: se deja solo alfanumérico.
        const code = String(this.code ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

        if (!code || this.working) return;

        this.localError = null;
        this.codeConfirmed.emit(code);
    }

    private _startCountdown(): void {

        this.secondsLeft = this.waitSeconds;

        if (this.timer) clearInterval(this.timer);

        this.timer = setInterval(() => {

            this.secondsLeft -= 1;

            if (this.secondsLeft <= 0) {

                clearInterval(this.timer);
                this.timer = null;
            }
        }, 1000);
    }
}
